import { DateRangePicker } from '@/components/common'
import type { DocumentStatus, DocumentFilters as Filters } from '@/types'
import { DOCUMENT_TYPE_LABELS } from '@/types'
import type { DocumentType } from '@/types'

const DOCUMENT_TYPES: { value: DocumentType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  ...Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
    value: value as DocumentType,
    label,
  })),
]

const DOCUMENT_STATUSES: { value: DocumentStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'processing', label: 'Processing' },
  { value: 'extracted', label: 'Extracted' },
  { value: 'reported', label: 'Reported' },
  { value: 'validated', label: 'Validated' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'manual_review', label: 'Manual Review' },
]

// The canonical Sumit vocabulary (HQ-859) written by expenses_sumit_sync and,
// since the vocabulary alignment, by expenses_sumit_import too. The legacy
// values below them are kept so historic rows stay filterable.
const SUMIT_STATUSES: { value: string; label: string }[] = [
  { value: '', label: 'All Sumit Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'synced', label: 'Synced' },
  { value: 'synced_annual_report', label: 'Synced (Annual Report)' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'duplicate_deleted', label: 'Duplicate (Deleted)' },
  { value: 'imported', label: 'Imported (legacy)' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'error', label: 'Error' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'none', label: 'None (No Sumit Status)' },
]

interface DocumentFiltersProps {
  filters: Filters
  onFiltersChange: (filters: Filters) => void
  className?: string
}

export function DocumentFilters({
  filters,
  onFiltersChange,
  className = '',
}: DocumentFiltersProps) {
  const handleTypeChange = (documentType: string) => {
    onFiltersChange({ ...filters, documentType: documentType || undefined })
  }

  const handleStatusChange = (documentStatus: string) => {
    onFiltersChange({ ...filters, documentStatus: documentStatus || undefined })
  }

  const handleSumitStatusChange = (sumitStatus: string) => {
    onFiltersChange({ ...filters, sumitStatus: sumitStatus || undefined })
  }

  const handleDateFromChange = (dateFrom: string) => {
    onFiltersChange({ ...filters, dateFrom: dateFrom || undefined })
  }

  const handleDateToChange = (dateTo: string) => {
    onFiltersChange({ ...filters, dateTo: dateTo || undefined })
  }

  const handleClearFilters = () => {
    onFiltersChange({})
  }

  const hasActiveFilters =
    filters.documentType || filters.documentStatus || filters.sumitStatus || filters.dateFrom || filters.dateTo

  return (
    <div
      className={`flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg ${className}`}
      data-testid="document-filters"
    >
      <div className="flex items-center gap-2">
        <label htmlFor="document-type" className="text-sm font-medium text-gray-700">
          Type:
        </label>
        <select
          id="document-type"
          value={filters.documentType || ''}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="filter-document-type"
        >
          {DOCUMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="document-status" className="text-sm font-medium text-gray-700">
          Status:
        </label>
        <select
          id="document-status"
          value={filters.documentStatus || ''}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="filter-document-status"
        >
          {DOCUMENT_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="sumit-status" className="text-sm font-medium text-gray-700">
          Sumit:
        </label>
        <select
          id="sumit-status"
          value={filters.sumitStatus || ''}
          onChange={(e) => handleSumitStatusChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="filter-sumit-status"
        >
          {SUMIT_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      <DateRangePicker
        dateFrom={filters.dateFrom || ''}
        dateTo={filters.dateTo || ''}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
      />

      {hasActiveFilters && (
        <button
          onClick={handleClearFilters}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 underline"
          data-testid="clear-filters"
        >
          Clear filters
        </button>
      )}
    </div>
  )
}
